import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  getMyProfile,
  updateMyContact,
  changeMyPassword,
  type User,
  type UpdateContactInput,
} from '../../api/profileApi.js';
import { getMyRoles } from '../../api/rolesApi.js';

/** E.164 mobile — mirrors backend F_PHONE_E164 (`^\+[1-9]\d{6,14}$`). */
const E164 = /^\+[1-9]\d{6,14}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PWD_MIN = 8;

interface ContactForm {
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  mobileNumber: string;
  emailAddress: string;
}

function toContactForm(u: User): ContactForm {
  return {
    firstName: u.firstName,
    middleName: u.middleName ?? '',
    lastName: u.lastName,
    displayName: u.displayName,
    mobileNumber: u.mobileNumber,
    emailAddress: u.emailAddress,
  };
}

/**
 * My Profile — self-service screen (M02 §29 / M02-G02).
 *
 * Lets the signed-in user view and edit their own contact details, change
 * their password (current password verified server-side, tenant policy +
 * history enforced), and manage two-factor authentication. No RBAC gate —
 * every authenticated user owns their profile.
 */
export function MyProfilePage(): ReactNode {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const userId = user?.id;

  const [form, setForm] = useState<ContactForm | null>(null);
  const [version, setVersion] = useState(1);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The role(s) the signed-in user currently holds. Read from the same
  // authoritative endpoints the Rights/User-admin screens use, so there is one
  // source of truth and no second copy of role data.
  const [roleNames, setRoleNames] = useState<readonly string[] | null>(null);
  // An unread role list is not "you have no role".
  const [roleError, setRoleError] = useState<string | null>(null);

  // Contact section state
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactNotice, setContactNotice] = useState<string | null>(null);
  const [savingContact, setSavingContact] = useState(false);

  // Password section state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwdError, setPwdError] = useState<string | null>(null);
  const [pwdNotice, setPwdNotice] = useState<string | null>(null);
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    getMyProfile(token)
      .then((u) => {
        if (!alive) return;
        setForm(toContactForm(u));
        setVersion(u.version);
      })
      .catch((e) => { if (alive) setLoadError((e as Error).message); });
    return () => { alive = false; };
  }, [userId, token]);

  // Resolve the user's active role assignments to their human-readable names.
  useEffect(() => {
    if (!userId) return;
    let alive = true;
    getMyRoles(token)
      .then((roles) => {
        if (!alive) return;
        setRoleNames(roles.map((r) => r.roleName || r.roleCode));
      })
      .catch((e) => { if (alive) setRoleError((e as Error).message); });
    return () => { alive = false; };
  }, [userId, token]);

  async function onSaveContact(e: FormEvent) {
    e.preventDefault();
    if (!form || !userId) return;
    setContactError(null);
    setContactNotice(null);

    if (!form.firstName.trim()) return setContactError(t('profile.me.err.firstName'));
    if (!form.lastName.trim()) return setContactError(t('profile.me.err.lastName'));
    if (!form.displayName.trim()) return setContactError(t('profile.me.err.displayName'));
    if (!EMAIL.test(form.emailAddress.trim())) return setContactError(t('profile.me.err.email'));
    if (!E164.test(form.mobileNumber.trim())) return setContactError(t('profile.me.err.mobile'));

    const opt = (v: string) => (v.trim() ? v.trim() : undefined);
    const body: UpdateContactInput = {
      version,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      displayName: form.displayName.trim(),
      mobileNumber: form.mobileNumber.trim(),
      emailAddress: form.emailAddress.trim(),
      ...(opt(form.middleName) ? { middleName: opt(form.middleName) } : {}),
    };
    setSavingContact(true);
    try {
      const next = await updateMyContact(body, token);
      setForm(toContactForm(next));
      setVersion(next.version);
      setContactNotice(t('profile.me.contactUpdated'));
    } catch (err) {
      setContactError((err as Error).message);
    } finally {
      setSavingContact(false);
    }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setPwdError(null);
    setPwdNotice(null);

    if (!oldPassword) return setPwdError(t('profile.me.err.currentPwd'));
    if (newPassword.length < PWD_MIN) return setPwdError(t('profile.me.err.pwdLen', { min: PWD_MIN }));
    if (newPassword === oldPassword) return setPwdError(t('profile.me.err.pwdSame'));
    if (newPassword !== confirmPassword) return setPwdError(t('profile.me.err.pwdMatch'));

    setSavingPwd(true);
    try {
      await changeMyPassword({ oldPassword, newPassword }, token);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPwdNotice(t('profile.me.pwdChanged'));
    } catch (err) {
      setPwdError((err as Error).message);
    } finally {
      setSavingPwd(false);
    }
  }

  if (loadError && !form) return <p role="alert">{loadError}</p>;
  if (!form) return <p>{t('profile.me.loading')}</p>;

  const set = (k: keyof ContactForm) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <main aria-label="my-profile">
      <h1>{t('profile.me.title')}</h1>

      <section aria-label="profile-role">
        <h2>{t('profile.me.yourRole')}</h2>
        {roleError ? (
          <p role="alert" data-testid="profile-role-error">{roleError}</p>
        ) : roleNames === null ? (
          <p data-testid="profile-role-loading">{t('profile.me.loading')}</p>
        ) : roleNames.length === 0 ? (
          <p data-testid="profile-role-none">{t('profile.me.noRole')}</p>
        ) : (
          <p data-testid="profile-role-value">{roleNames.join(', ')}</p>
        )}
      </section>

      <section aria-label="profile-contact">
        <h2>{t('profile.me.contact')}</h2>
        <form onSubmit={onSaveContact} aria-label="profile-contact-form" noValidate>
          <label htmlFor="p-first">{t('profile.me.firstName')}</label>
          <input id="p-first" value={form.firstName} onChange={set('firstName')} />
          <label htmlFor="p-middle">{t('profile.me.middleName')}</label>
          <input id="p-middle" value={form.middleName} onChange={set('middleName')} />
          <label htmlFor="p-last">{t('profile.me.lastName')}</label>
          <input id="p-last" value={form.lastName} onChange={set('lastName')} />
          <label htmlFor="p-display">{t('profile.me.displayName')}</label>
          <input id="p-display" value={form.displayName} onChange={set('displayName')} />
          <label htmlFor="p-mobile">{t('profile.me.mobile')}</label>
          <input id="p-mobile" value={form.mobileNumber} onChange={set('mobileNumber')} />
          <label htmlFor="p-email">{t('profile.me.email')}</label>
          <input id="p-email" type="email" value={form.emailAddress} onChange={set('emailAddress')} />
          {contactError ? <p role="alert">{contactError}</p> : null}
          {contactNotice ? <p role="status">{contactNotice}</p> : null}
          <button type="submit" disabled={savingContact}>{t('profile.me.saveContact')}</button>
        </form>
      </section>

      <section aria-label="profile-password">
        <h2>{t('profile.me.changePassword')}</h2>
        <form onSubmit={onChangePassword} aria-label="change-password-form">
          <label htmlFor="p-old">{t('profile.me.currentPassword')}</label>
          <input id="p-old" type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
          <label htmlFor="p-new">{t('profile.me.newPassword')}</label>
          <input id="p-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
          <label htmlFor="p-confirm">{t('profile.me.confirmPassword')}</label>
          <input id="p-confirm" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
          {pwdError ? <p role="alert">{pwdError}</p> : null}
          {pwdNotice ? <p role="status">{pwdNotice}</p> : null}
          <button type="submit" disabled={savingPwd}>{t('profile.me.changePassword')}</button>
        </form>
      </section>

      <section aria-label="profile-mfa">
        <h2>{t('profile.me.mfa')}</h2>
        <p>{t('profile.me.mfaHint')}</p>
        <Link to="/auth/mfa/enrol">{t('profile.me.mfaSetup')}</Link>
      </section>
    </main>
  );
}
